import { useCallback } from 'react'
import { useRouter } from 'next/router'
import { InventoryItem, Venue, VenueEvent } from 'types/model'
import {
  VenueLayout,
  VenueLayoutSection,
  VenueLayoutTable,
} from 'types/model/venue-layout'

import { Link } from '@elements/link'
import { H1 } from '@elements/headings'
import { EventCarousel } from '@modules/event/controllers/event-carousel'
import { InventoryList } from '@modules/inventory/controllers/inventory-list'
import { VenueAboutSection } from './venue-about-section'

import { breakpoints } from '@utils/breakpoints'
import { useBreakpoint } from '@hooks/breakpoints'
import { ArrowRightIcon } from '@heroicons/react/24/outline'

export function VenueHomeView({
  venue,
  events,
  tickets,
  packages,
  layouts,
  sections,
  tables,
  initialInventoryDate,
  inventoryCalendarMinDate,
  inventoryCalendarMaxDate,
}: {
  venue: Venue
  events: VenueEvent[]
  tickets: InventoryItem[]
  packages: InventoryItem[]
  layouts: VenueLayout[]
  sections: VenueLayoutSection[]
  tables: VenueLayoutTable[]
  initialInventoryDate: string
  inventoryCalendarMinDate: number
  inventoryCalendarMaxDate: number
}) {
  const router = useRouter()
  const mdScreen = useBreakpoint(breakpoints.md)

  const handleCarouselClick = useCallback(
    async (eventId: string) => {
      await router.push(`/e/${eventId}`)
    },
    [router]
  )

  return (
    <div className='-mb-6'>
      {!!events.length && (
        <div className='px-3 py-6 border-b shadow-sm sm:p-6 lg:px-8 lg:py-10 bg-card border-standard-light'>
          <div className='max-w-6xl mx-auto space-y-3'>
            <div className='flex items-center justify-between w-full'>
              <H1 className='heading-xl sm:heading-2xl'>
                <span className='hidden sm:inline-block'>Upcoming</span> Events
              </H1>
              <Link
                href={`/v/${venue.slug}/events`}
                className='flex items-center justify-end space-x-2'
              >
                <span>See All</span> <ArrowRightIcon className='w-4 h-4' />
              </Link>
            </div>
            <EventCarousel
              onCardClick={handleCarouselClick}
              venueId={venue.id}
              venue={venue}
              events={events}
            />
          </div>
        </div>
      )}
      <div className='px-3 py-6 border-b shadow-sm sm:p-6 lg:px-8 lg:py-10 border-standard-light bg-inherit'>
        <div className='max-w-6xl mx-auto space-y-3 bg-inherit min-h-[80vh]'>
          <H1 className='heading-xl sm:heading-2xl'>Booking</H1>
          <InventoryList
            venue={venue}
            showEvent={false}
            showDatePicker
            stickyMap={mdScreen}
            initialDate={initialInventoryDate}
            inventoryCalendarMinDate={inventoryCalendarMinDate}
            inventoryCalendarMaxDate={inventoryCalendarMaxDate}
            venueEventsForInventoryDay={events}
            tickets={tickets}
            packages={packages}
            layouts={layouts}
            sections={sections}
            tables={tables}
          />
        </div>
      </div>
      <VenueAboutSection venue={venue} />
    </div>
  )
}
